import { useEffect, useMemo, useRef } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Button } from 'foundations-components/transitional/components/Button';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import { Link } from 'foundations-components/transitional/components/Link';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { getAppSettingsUrl } from 'integrations-lib/utils/connectedAppUtils';
import { useDetailsPageContext } from '../../context/DetailsPageContext';
import { usePollForInstallStatus } from '../../hooks/usePollForInstallStatus';
import { getAppAppCardsUrl, getAppFeatureDiscoveryUrl, getAppOverviewUrl, getConnectedAppsUrl } from '../../utils/DetailsPageUrls';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
function InstallSuccessButtons({
  appId,
  features,
  isUIE,
  portalId,
  onClose,
  trackInteraction
}) {
  var _features$some;
  const hasFeatureDiscoveryCards = (_features$some = features === null || features === void 0 ? void 0 : features.some(feature => {
    var _feature$featureLocat;
    return (_feature$featureLocat = feature.featureLocation) === null || _feature$featureLocat === void 0 ? void 0 : _feature$featureLocat.includes('DISCOVERY_CARD');
  })) !== null && _features$some !== void 0 ? _features$some : false;
  return /*#__PURE__*/_jsxs(ButtonWrapper, {
    marginBlockStart: "400",
    justify: "center",
    children: [/*#__PURE__*/_jsx(Button, {
      use: "primary",
      href: hasFeatureDiscoveryCards ? getAppFeatureDiscoveryUrl(appId, portalId) : getAppOverviewUrl(appId, portalId),
      onClick: () => trackInteraction({
        action: 'click feature discovery link'
      }),
      marginBlockStart: "400",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `detailsPage.install.completeStep.${hasFeatureDiscoveryCards ? 'featureDiscovery' : 'overview'}`
      })
    }), isUIE ? /*#__PURE__*/_jsx(Button, {
      href: getAppAppCardsUrl(appId, portalId),
      onClick: () => trackInteraction({
        action: 'click app cards link'
      }),
      marginBlockStart: "400",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.install.completeStep.uie"
      })
    }) : /*#__PURE__*/_jsx(Button, {
      onClick: onClose,
      marginBlockStart: "400",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.install.completeStep.close"
      })
    })]
  });
}
function LoadingState({
  name
}) {
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Text, {
      as: "h1",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.install.completeStep.loadingHeader",
        options: {
          name
        }
      })
    }), /*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      marginBlockStart: "300",
      children: /*#__PURE__*/_jsx(LoadingSpinner, {})
    })]
  });
}
function ErrorState({
  name,
  onClose
}) {
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Text, {
      as: "h1",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.install.completeStep.errorHeader",
        options: {
          name
        }
      })
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      alignItems: "center",
      marginBlockStart: "300",
      children: [/*#__PURE__*/_jsx(Alert, {
        type: "warning",
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.install.completeStep.errorMessageTitle"
        }),
        children: /*#__PURE__*/_jsx(Text, {
          children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "detailsPage.install.completeStep.errorMessage_jsx",
            options: {
              href: getConnectedAppsUrl(),
              external: true
            },
            elements: {
              Link
            }
          })
        })
      }), /*#__PURE__*/_jsx(ButtonWrapper, {
        marginBlockStart: "400",
        justify: "center",
        children: /*#__PURE__*/_jsx(Button, {
          onClick: onClose,
          marginBlockStart: "400",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.install.completeStep.close"
          })
        })
      })]
    })]
  });
}
function EmbeddedSuccessState({
  name,
  portalId,
  appId,
  isUIE,
  onClose,
  trackInteraction
}) {
  const subheaderMessage = isUIE ? 'detailsPage.install.completeStep.embedded.subheaderWithAppCards_jsx' : 'detailsPage.install.completeStep.embedded.subheader_jsx';
  const subheaderOptions = isUIE ? {
    appFeaturesLink: {
      href: `${getAppSettingsUrl({
        appId,
        portalId
      })}/featureDiscovery`,
      onClick: () => trackInteraction({
        action: 'click feature discovery link'
      }),
      external: true
    },
    appCardsLink: {
      href: `${getAppSettingsUrl({
        appId,
        portalId
      })}/app-cards`,
      onClick: () => trackInteraction({
        action: 'click app cards link'
      }),
      external: true
    }
  } : {
    appFeaturesLink: {
      href: `${getAppSettingsUrl({
        appId,
        portalId
      })}/featureDiscovery`,
      onClick: () => trackInteraction({
        action: 'click feature discovery link'
      }),
      external: true
    }
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Text, {
      as: "h1",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.install.completeStep.completeHeader",
        options: {
          name
        }
      })
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      alignItems: "center",
      marginBlockStart: "300",
      children: [/*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: subheaderMessage,
        elements: {
          Link
        },
        options: subheaderOptions
      }), /*#__PURE__*/_jsx(ButtonWrapper, {
        marginBlockStart: "400",
        justify: "center",
        children: /*#__PURE__*/_jsx(Button, {
          use: "primary",
          onClick: onClose,
          marginBlockStart: "400",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.install.completeStep.close"
          })
        })
      })]
    })]
  });
}
function NonEmbeddedSuccessState({
  name,
  children
}) {
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Text, {
      as: "h1",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.install.completeStep.completeHeader",
        options: {
          name
        }
      })
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      alignItems: "center",
      marginBlockStart: "300",
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.install.completeStep.subheader"
      }), children]
    })]
  });
}
export function InstallSuccessStep({
  appId,
  features,
  isUIE,
  name,
  portalId,
  renderedHeaderIcons,
  onClose,
  listingId,
  isEmbedded,
  skipPoll = false
}) {
  const {
    trackInteraction,
    appContext
  } = useDetailsPageContext();

  // Refs to ensure tracking only fires once
  const hasTrackedError = useRef(false);
  const hasTrackedSuccess = useRef(false);

  // When skipPoll is true (install already confirmed upstream), skip the
  // polling query entirely and treat the install as successful.
  const {
    installed: polledInstalled,
    loading,
    error: pollError
  } = usePollForInstallStatus({
    listingId: skipPoll ? undefined : listingId,
    appContext
  });
  const installed = skipPoll ? true : polledInstalled;
  const isLoading = skipPoll ? false : loading;
  const error = skipPoll ? null : pollError;

  // Track error state
  useEffect(() => {
    if (error && !hasTrackedError.current) {
      hasTrackedError.current = true;
      trackInteraction({
        action: 'error installing app'
      });
    }
  }, [error, trackInteraction]);

  // Track successful installation
  useEffect(() => {
    if (installed === true && !hasTrackedSuccess.current) {
      hasTrackedSuccess.current = true;
      trackInteraction({
        action: 'complete third party sign in'
      });
    }
  }, [installed, trackInteraction]);

  // Determine which state to render
  const renderedContent = useMemo(() => {
    if (error) {
      return /*#__PURE__*/_jsx(ErrorState, {
        name: name,
        onClose: onClose
      });
    }
    if (installed === true) {
      if (isEmbedded) {
        return /*#__PURE__*/_jsx(EmbeddedSuccessState, {
          name: name,
          appId: appId,
          portalId: portalId,
          isUIE: isUIE,
          onClose: onClose,
          trackInteraction: trackInteraction
        });
      }
      return /*#__PURE__*/_jsx(NonEmbeddedSuccessState, {
        name: name,
        children: /*#__PURE__*/_jsx(InstallSuccessButtons, {
          appId: appId,
          features: features,
          isUIE: isUIE,
          portalId: portalId,
          onClose: onClose,
          trackInteraction: trackInteraction
        })
      });
    }

    // Loading state (when polling or initial load)
    if (isLoading) {
      return /*#__PURE__*/_jsx(LoadingState, {
        name: name
      });
    }

    // Fallback - should not reach this in normal flow
    return /*#__PURE__*/_jsx(LoadingState, {
      name: name
    });
  }, [error, features, installed, isLoading, name, isEmbedded, appId, isUIE, portalId, onClose, trackInteraction]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [renderedHeaderIcons, renderedContent]
  });
}